import * as Sentry from '@sentry/react';
import { getPerfArms, getPerfArmProperties } from 'src/lib/perf/perfArms';
import type { ErrorEvent } from '@sentry/react';
import { AxiosError, CanceledError, isAxiosError, isCancel } from 'axios';
import { sentryConfig } from './sentry.constants';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../lib/logging';

/**
 * True only when axios's configured timeout actually fired — distinguishes a real
 * deadline-exceeded error from a TCP/proxy-level connection abort (both surface as
 * ECONNABORTED). Axios sets the message to "timeout of {ms}ms exceeded" on real
 * timeouts and to "Request aborted" on TCP aborts; checking the message avoids
 * fingerprinting flaky-connection users into the axios-timeout monitoring bucket.
 *
 * `!!err.config?.timeout` alone is insufficient: the global singleton sets a 30s
 * default, so every request has `config.timeout` regardless of which abort path
 * triggered.
 */
export const isAxiosTimeoutError = (err: unknown): err is AxiosError =>
  isAxiosError(err) &&
  !err.response &&
  err.code === 'ECONNABORTED' &&
  typeof err.message === 'string' &&
  err.message.toLowerCase().includes('timeout');

const SENSITIVE_PAYLOAD_KEYS = [
  'payment_method_id',
  'payment_intent_id',
  'paymentMethodId',
  'paymentIntentId',
  'card',
  'cvv',
  'cvc',
  // BUG-800: the accept of an emailed link carries the password the person typed.
  'password',
];

/**
 * Replaces the value of any SENSITIVE_PAYLOAD_KEYS entry with [Filtered]. Returns a
 * shallow clone so the live request object isn't mutated. Defense-in-depth for
 * Stripe payment identifiers and passwords attached to event.extra.request via
 * axios.service.ts.
 */
export const scrubSensitivePayload = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  const cloned: Record<string, unknown> = { ...source };
  for (const key of SENSITIVE_PAYLOAD_KEYS) {
    if (key in cloned) cloned[key] = '[Filtered]';
  }
  return cloned;
};

// BUG-800: X-Invitation-Token carries the token of an emailed invitation link.
const SENSITIVE_HEADER_KEYS = [
  'authorization',
  'x-api-key',
  'x-invitation-token',
];

const REDACTED_VALUE = '[REDACTED]';

/**
 * Depth cap: event.extra holds the live axios config, whose adapter/transport
 * references can cycle back into objects already visited.
 */
const MAX_SCRUB_DEPTH = 8;

/**
 * Replaces the value of any credential-bearing header with [REDACTED] at every depth
 * of the given value, returning clones so the live request config isn't mutated. The
 * key survives so triage still shows the header was present.
 *
 * Matching is case-insensitive: call sites spell the header differently
 * (`authorization`, `Authorization`, `X-Api-Key`) and axios only normalizes casing
 * once the config is merged into a request, which is after AxiosService has already
 * attached the raw config to event.extra.axiosConfig.
 */
export const scrubSensitiveHeaders = (
  value: unknown,
  depth: number = 0,
): unknown => {
  if (!value || typeof value !== 'object' || depth >= MAX_SCRUB_DEPTH) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubSensitiveHeaders(item, depth + 1));
  }
  const source = value as Record<string, unknown>;
  const cloned: Record<string, unknown> = { ...source };
  for (const key of Object.keys(cloned)) {
    cloned[key] = SENSITIVE_HEADER_KEYS.includes(key.toLowerCase())
      ? REDACTED_VALUE
      : scrubSensitiveHeaders(cloned[key], depth + 1);
  }
  return cloned;
};

/**
 * Runs every scrubber over the diagnostic context AxiosService attaches to captured
 * exceptions. Exported so it can be unit-tested without re-initializing Sentry.
 *
 * Never throws: a throw inside `beforeSend` makes Sentry drop the event, so losing
 * the error entirely would be worse than an unscrubbed field.
 */
export const scrubEventExtra = (event: ErrorEvent): void => {
  try {
    const extra = event.extra as Record<string, unknown> | undefined;
    if (!extra) return;

    if (extra.request) {
      extra.request = scrubSensitivePayload(extra.request);
    }
    if (extra.axiosConfig && typeof extra.axiosConfig === 'object') {
      const config = extra.axiosConfig as Record<string, unknown>;
      if (config.data !== undefined) {
        config.data = scrubSensitivePayload(config.data);
      }
    }
    for (const key of Object.keys(extra)) {
      extra[key] = scrubSensitiveHeaders(extra[key]);
    }
  } catch {
    // Swallowed on purpose, see the note above about dropped events.
  }
};

// Variable to store the Sentry instance
let sentryInitialized = false;

export type tTransientAxiosError = AxiosError & {
  code: 'ERR_NETWORK' | 'ERR_CANCELED' | 'ECONNABORTED';
};

/**
 * True for errors that self-heal on retry and should NOT reach Sentry:
 *   - Axios CanceledError (AbortController.abort())
 *   - AxiosError with no HTTP response AND code in {ERR_NETWORK, ERR_CANCELED, ECONNABORTED}
 *
 * Errors with `response` populated (real 4xx/5xx) are NOT transient and still flow through.
 */
export const isTransientAxiosError = (
  err: unknown,
): err is tTransientAxiosError | CanceledError<unknown> => {
  if (isCancel(err)) return true;
  if (!isAxiosError(err) || err.response) return false;
  return (
    err.code === 'ERR_NETWORK' ||
    err.code === 'ERR_CANCELED' ||
    err.code === 'ECONNABORTED'
  );
};

/**
 * Initializes Sentry with the provided configuration
 * @returns The Sentry instance
 */
function initializeSentry(): typeof Sentry {
  if (!sentryInitialized) {
    try {
      // Basic Sentry configuration
      Sentry.init({
        enabled: sentryConfig.enabled,
        dsn: sentryConfig.dsn,
        tunnel: sentryConfig.tunnel,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.browserProfilingIntegration(),
          ...(getPerfArms().replayArm === 'recorded' ? [Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          })] : []),
        ],
        initialScope: { tags: getPerfArmProperties() },
        environment: sentryConfig.environment,
        tracesSampleRate: sentryConfig.tracesSampleRate,
        replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
        replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
        tracePropagationTargets: [
          'localhost',
          /^\//,
          /^https:\/\/[^/]*\.leadsales\.com/,
        ],
        beforeSend(event, hint) {
          // Surface axios timeouts (ECONNABORTED + "timeout" in message) as warnings so the
          // post-deploy monitoring plan for the 30s axios.singleton timeout can see them.
          // Other transient errors (network drops, intentional cancels, TCP aborts) remain
          // dropped — high-volume noise that would inflate the timeout alert.
          if (isAxiosTimeoutError(hint?.originalException)) {
            event.level = 'warning';
            event.fingerprint = ['axios-timeout', '{{ default }}'];
          } else if (isTransientAxiosError(hint?.originalException)) {
            return null;
          }
          // Scrub Stripe payment identifiers from the captured request body and bearer
          // tokens / API keys from the captured request headers before shipping to
          // Sentry. axios.service.ts attaches the POST payload and the whole request
          // config to event.extra for diagnostic context, so billing 3DS calls would
          // carry confidential identifiers and auth'd calls would carry credentials.
          scrubEventExtra(event);
          return event;
        },
      });

      LLog.info(
        {
          component: LOG_COMPONENTS.API,
          method: 'init',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'Sentry initialized successfully',
      );

      sentryInitialized = true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      LLog.error(
        {
          component: LOG_COMPONENTS.API,
          method: 'init',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error initializing Sentry',
        error instanceof Error ? error : new Error(errorMessage),
      );
    }
  }

  return Sentry;
}

/**
 * Gets the Sentry instance, initializing it if necessary
 * @returns The Sentry instance
 */
function getSentryInstance(): typeof Sentry {
  if (!sentryInitialized) {
    return initializeSentry();
  }

  return Sentry;
}

// Export the singleton instance
export const sentry = getSentryInstance();
